import { IFilter } from "../interfaces/IFilter";
import { HttpRequest } from "../utils";

export const httpRequest = new HttpRequest();

export const respond = async (
  id: string,
  data: {
    confirm: boolean;
    respondText: string;
  },
) => {
  return httpRequest.put(`/inv/invoice/${id}/respond`, data);
};

export const create = async (data: any) => {
  return httpRequest.post("/inv/invoice", data);
};

export const update = async (id: string, data: any) => {
  return httpRequest.put(`/inv/invoice/${id}`, data);
};

export const get = async (id: string) => {
  return httpRequest.get(`/inv/invoice/${id}`);
};

export const received = async ({
  offset,
  filter,
  sort,
}: IFilter & {
  sort?: { field: string; dir: "asc" | "desc" } | null;
}) => {
  return httpRequest.get("/inv/invoice/received", {
    offset,
    filter,
    ...(sort?.field && sort?.dir ? { sort } : {}),
  });
};

export const remove = async (id: string) => {
  return httpRequest.del(`/inv/invoice/${id}`);
};

export const send = async (id: string, data: any) => {
  return httpRequest.put(`/inv/invoice/${id}/send`, data);
};

/** Re-deliver a PDF copy by e-mail; never changes the invoice. `email` overrides the buyer contact. */
export const deliverCopy = async (id: string, data?: { email?: string }) => {
  return httpRequest.post(`/inv/invoice/${id}/deliver-copy`, data);
};

export const sent = async ({
  offset,
  filter,
  sort,
}: IFilter & {
  sort?: { field: string; dir: "asc" | "desc" } | null;
}) => {
  return httpRequest.get(`/inv/invoice/sent`, {
    offset,
    filter,
    ...(sort?.field && sort?.dir ? { sort } : {}),
  });
};

export const pay = async (data: any) => {
  return httpRequest.post(`/pay/invoice`, data);
};

export const history = async ({ offset, filter }: IFilter) => {
  return httpRequest.get(`/inv/invoice/history`, {
    offset,
    filter,
  });
};

export const historyCreate = async (data: IHistoryCreate) => {
  return httpRequest.post(`/inv/invoice/history`, data);
};

export const close = async ({ offset, filter }: IFilter) => {
  return httpRequest.get(`/inv/invoice/closed`, {
    offset,
    filter,
  });
};

// Phase C — closed invoices annotated with collected-fee status (feeAmount,
// feeCurrency, feeStatus per row).
export const closeWithFee = async ({ offset, filter }: IFilter) => {
  return httpRequest.get(`/inv/invoice/closed`, {
    offset,
    filter,
    withFee: 1,
  });
};

// Phase C — the tenant's payable fees + verified default RECEIVE account.
export const feesUnpaid = async () => {
  return httpRequest.get(`/inv/fees/unpaid`);
};

// Phase C (corrected) — the END-CUSTOMER pays fees owed to ONE collector tenant:
// debit = their own paying account, credit = the tenant RECEIVE account (resolved
// server-side from creditTenantId). Returns { url?, qr_image?, paymentId } like
// InvoiceApi.pay.
export const feesPay = async (data: {
  collectedFeeIds: string[];
  creditTenantId: string;
  debitAccountId: string;
  debitAccountBank: string;
  debitAccountName: string;
  debitAccountNumber: string;
  debitAccountCurrency: string;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return httpRequest.post(`/inv/fees/pay`, data as any);
};

type IHistoryCreate = {
  receiverBusinessId: string | undefined | null;
  paymentMethod: string | undefined | null;
  totalAmount: number | undefined | null;
  trxRefCode: string | undefined | null;
  invoices:
    | any[]
    | {
        invoiceId: undefined | string;
        amount: number | undefined;
      }[];
  files: any[];
};

export const invoice_list = async (data: any) => {
  return httpRequest.get("/inv/invoice/history/invoice_list", data);
};

export const pdf = async (id: string) => {
  return httpRequest.get(`/inv/invoice/${id}/pdf`);
};

export type IExtensionContext = {
  invoiceId: string;
  refCode: string;
  counterparty: { id: string; profileName?: string; profileNameEng?: string; businessName?: string; businessNameEng?: string } | null;
  dueDay: string | null;
  today: string;
  /** > 0 overdue, < 0 days left. */
  overdueDays: number | null;
  amountToPay: number;
  extensionCount: number;
  maxExtensions: number;
  maxExtensionDays: number;
  minAdditionalDays: number;
  canExtend: boolean;
  lateFee: { enabled: boolean; accrued: number };
};

export const extensionContext = async (invoiceId: string): Promise<IExtensionContext> => {
  return httpRequest.get(`/inv/invoice/${invoiceId}/extension-context`);
};

export const extendPayment = async (invoiceId: string, data: any) => {
  return httpRequest.post(`/inv/invoice/${invoiceId}/extend-payment`, data);
};

export type IAmendmentLineInput = {
  parentVariantId: string;
  variantId: string;
  warehouseId: string;
  discountType?: string | null;
  discountValue?: number | null;
  quantity: number;
  price: number;
  /** Display only — lets the buyer's comparison name a new item. */
  name?: string | null;
  skuCode?: string | null;
};

export type IAmendmentAdditionalLineInput = {
  image?: string | null;
  name: string;
  unit: string;
  price: number;
  discountType?: string | null;
  discountValue?: number | null;
  quantity: number;
};

export type IAmendmentPending = {
  id: string;
  refCode: string;
  reason: string;
  previousTotal: number;
  /** Accept-time total (recomputed when API enrichment is present). */
  newTotal: number;
  /** Present when stored proposal total disagrees with the recompute. */
  storedNewTotal?: number;
  previousVat?: number;
  newVat?: number;
  vatMode?: "INCLUSIVE" | "EXCLUSIVE";
  lines: {
    lines?: IAmendmentLineInput[] | null;
    additionalLines?: IAmendmentAdditionalLineInput[] | null;
    discountType?: string | null;
    discountValue?: number | null;
    shippingAmount?: number | null;
    description?: string | null;
  };
  proposedAt: string;
};

export type IAmendmentHistoryItem = {
  id: string;
  refCode: string;
  status: "ACCEPTED" | "REJECTED" | "WITHDRAWN";
  reason: string;
  previousTotal: number;
  newTotal: number;
  proposedAt: string;
  respondedAt: string | null;
  rejectionReason: string | null;
};

export type IAmendability = {
  enabled: boolean;
  amendable: boolean;
  reasons: string[];
  pending?: IAmendmentPending;
  history?: IAmendmentHistoryItem[];
};

export type IProposeAmendmentBody = {
  reason: string;
  lines?: IAmendmentLineInput[];
  additionalLines?: IAmendmentAdditionalLineInput[];
  discountType?: string | null;
  discountValue?: number | null;
  shippingAmount?: number | null;
  description?: string | null;
  dryRun?: boolean;
};

export const amendability = async (id: string): Promise<IAmendability> => {
  return httpRequest.get(`/inv/invoice/${id}/amendability`);
};

export const proposeAmendment = async (id: string, body: IProposeAmendmentBody) => {
  return httpRequest.post(`/inv/invoice/${id}/amendments`, body);
};

export const withdrawAmendment = async (id: string, amendmentId: string) => {
  return httpRequest.post(`/inv/invoice/${id}/amendments/${amendmentId}/withdraw`);
};

export const respondAmendment = async (
  id: string,
  amendmentId: string,
  body: { accept: boolean; rejectionReason?: string },
) => {
  return httpRequest.post(`/inv/invoice/${id}/amendments/${amendmentId}/respond`, body);
};
